import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { first, switchMap, take, tap } from 'rxjs/operators';

import { MessagesConstants } from '@shared/constants/messages.constants';
import {
  CreateDriverBlendedPatientDistributionInput,
  createDriverBlendedPatientDistributionMutation,
  GqlService,
  UpdateDriverBlendedPatientDistributionInput,
  updateDriverBlendedPatientDistributionMutation,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { PatientCurveModel, PatientCurveStore } from './patient-curve.store';

@Injectable({ providedIn: 'root' })
export class PatientCurveService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private patientCurveStore = inject(PatientCurveStore);

  async add(
    patient: CreateDriverBlendedPatientDistributionInput
  ): Promise<GraphqlResponse<createDriverBlendedPatientDistributionMutation>> {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.createDriverBlendedPatientDistribution$({
        name: patient.name,
        patient_group_ids: patient.patient_group_ids,
      })
    );

    if (success) {
      await firstValueFrom(this.get().pipe(first()));
      this.overlayService.success(MessagesConstants.PATIENT_CURVES.SUCCESSFULLY_CREATED);
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }

  get() {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.patientCurveStore.remove(() => true);

        return this.gqlService.listDriverPatientGroups$().pipe(
          tap(({ data, errors, success }) => {
            let flag = false;

            if (success && data && data?.length > 0) {
              this.patientCurveStore.set(
                data.map((x) => {
                  if (!x.is_blended && !flag) {
                    flag = true;

                    return { ...x, showLine: true };
                  }

                  return { ...x, showLine: false };
                }) as PatientCurveModel[]
              );
            } else if (!success) {
              this.overlayService.error(errors);
            }
          })
        );
      })
    );
  }

  async remove(patient: PatientCurveModel) {
    let successOverall = false;
    let errorsOverall: string | string[] | undefined = [];

    if (patient.is_blended && patient.patient_group_id) {
      const { errors, success } = await firstValueFrom(
        this.gqlService.removeDriverBlendedPatientDistribution$(patient.patient_group_id)
      );

      successOverall = success;
      errorsOverall = errors;
    } else if (!patient.is_blended) {
      const { errors, success } = await firstValueFrom(
        this.gqlService.removePatientGroupDriver$(patient.driver_setting_id)
      );

      successOverall = success;
      errorsOverall = errors;
    }

    if (successOverall) {
      this.overlayService.success(`${patient.name} successfully removed!`);
      await firstValueFrom(this.get().pipe(take(1)));
    } else {
      this.overlayService.error(errorsOverall);
    }

    return { errorsOverall, successOverall };
  }

  async update(
    patient: UpdateDriverBlendedPatientDistributionInput
  ): Promise<GraphqlResponse<updateDriverBlendedPatientDistributionMutation>> {
    const { data, errors, success } = await firstValueFrom(
      this.gqlService.updateDriverBlendedPatientDistribution$({
        blended_group_id: patient.blended_group_id,
        name: patient.name,
        patient_group_ids: patient.patient_group_ids,
      })
    );

    if (success) {
      this.overlayService.success(`${patient.name} successfully updated!`);
      await firstValueFrom(this.get().pipe(take(1)));
    } else {
      this.overlayService.error(errors);
    }

    return { data, errors, success };
  }
}
